/** @jsx jsx */

import { jsx } from '@emotion/core';
import { isNumber, isNaN, isFinite } from 'lodash';

import { abbreviateNumber, relativeRound } from '@zapier/common-utils';

type Props = {
  /** Number value */
  children: number;
  /**
   * Label to show when value is infinite
   *
   * @default Infinity
   */
  infinityLabel?: string;
  /**
   * Abbreviate number using SI suffix
   *
   * @default false
   */
  isAbbreviated?: boolean;
  /**
   * Use friendly relative rounding
   *
   * @default false
   */
  isFriendly?: boolean;
  /**
   * Treat -1 as infinite
   *
   * @default false
   */
  isNegativeOneInfinity?: boolean;
  /**
   * Maximum number of decimals to show
   *
   * @default 2
   */
  maxDecimals?: number;
  /**
   * Minimum number of decimals to show
   *
   * @default 0
   */
  minDecimals?: number;
  /**
   * Label to show when value is not a number
   *
   * @default NaN
   */
  nanLabel?: string;
};

const getDisplayValue = ({
  children,
  isFriendly,
  isAbbreviated,
  maxDecimals,
  minDecimals,
}: Props) => {
  // Finite Number values
  if (isFriendly) {
    // Relative round first if required
    return relativeRound(children);
  }

  if (isAbbreviated) {
    return abbreviateNumber(children);
  } else {
    // Format to Locale if we are not abbreviating
    return children.toLocaleString(undefined, {
      maximumFractionDigits: maxDecimals,
      minimumFractionDigits: minDecimals,
    });
  }
};

/**
 * Formats a number value for display
 */
const Number = (props: Props) => {
  const {
    children,
    infinityLabel,
    isAbbreviated,
    isNegativeOneInfinity,
    nanLabel,
  } = props;

  // Non-number values
  if (isNaN(children) || !isNumber(children)) {
    return <span>{nanLabel}</span>;
  }

  // Infinite number values
  if (
    !isFinite(children) ||
    // special case for treating -1 as Infinity
    (isNegativeOneInfinity && children === -1)
  ) {
    return <span>{infinityLabel}</span>;
  }

  const displayValue = getDisplayValue(props);
  return (
    <span title={isAbbreviated ? children.toLocaleString() : undefined}>
      {displayValue}
    </span>
  );
};

Number.defaultProps = {
  isFriendly: false,
  maxDecimals: 2,
  minDecimals: 0,
  nanLabel: 'NaN',
  infinityLabel: 'Infinity',
  isAbbreviated: false,
  isNegativeOneInfinity: false,
};

export default Number;
